import React from 'react';
import { useRestTimer } from '../contexts/RestTimerContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Timer, SkipForward } from 'lucide-react';
import { TA } from './tour/tourAnchors';

export default function RestTimerPill() {
  const { restTimer, restTimerRunning, skipRestTimer, adjustRestTimer, restExerciseName } = useRestTimer();
  const location = useLocation();
  const navigate = useNavigate();

  if (!restTimerRunning || restTimer <= 0) return null;

  const isOnWorkout = location.pathname === '/workout';
  const mins = Math.floor(restTimer / 60);
  const secs = restTimer % 60;
  const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;

  return (
    <div
      className="fixed z-40 left-1/2 -translate-x-1/2"
      // Mobile: float above the bottom nav (60px + safe-area + 8px gap).
      // Desktop: no bottom nav, sit 24px from the bottom — coach/client both
      // need the rest timer at-a-glance, was previously md:hidden which left
      // desktop users with no rest feedback at all.
      style={{ bottom: 'max(24px, calc(60px + env(safe-area-inset-bottom, 0px) + 8px))' }}
      data-tour-anchor={TA.WORKOUT_REST_TIMER}
      data-testid="rest-timer-display"
    >
      <div
        className="flex items-center gap-2 px-4 py-2 rounded-full shadow-lg"
        style={{
          background: 'linear-gradient(135deg, #FF5C00, #FF8C42)',
          boxShadow: '0 4px 20px rgba(255,92,0,0.4), 0 2px 8px rgba(0,0,0,0.3)',
          animation: 'rest-pill-enter 0.3s ease-out',
          fontFamily: 'Barlow Condensed, sans-serif',
        }}
      >
        {/* Timer + exercise name */}
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => { if (!isOnWorkout) navigate('/workout'); }}
        >
          <Timer className="h-5 w-5 text-white flex-shrink-0" />
          <span className="text-lg font-bold text-white font-mono tracking-tight">{timeStr}</span>
          {restExerciseName && (
            <>
              <span className="text-white/50">·</span>
              <span className="text-xs font-medium text-white/90 truncate max-w-[100px]">{restExerciseName}</span>
            </>
          )}
        </div>
        {/* Controls */}
        <div className="flex items-center gap-1.5 ml-0.5">
          <button
            onClick={(e) => { e.stopPropagation(); adjustRestTimer(-15); }}
            className="min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 md:w-7 md:h-7 rounded-full bg-white/20 flex items-center justify-center text-xs md:text-[10px] font-bold text-white hover:bg-white/30 transition-colors active:scale-[0.93]"
          >-15</button>
          <button
            onClick={(e) => { e.stopPropagation(); adjustRestTimer(15); }}
            className="min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 md:w-7 md:h-7 rounded-full bg-white/20 flex items-center justify-center text-xs md:text-[10px] font-bold text-white hover:bg-white/30 transition-colors active:scale-[0.93]"
          >+15</button>
          <button
            onClick={(e) => { e.stopPropagation(); skipRestTimer(); }}
            className="min-h-[44px] md:min-h-0 md:h-7 px-3 md:px-2.5 rounded-full bg-white/25 flex items-center gap-1 text-xs md:text-[10px] font-bold text-white hover:bg-white/35 transition-colors active:scale-[0.93]"
            data-testid="rest-timer-skip"
          >
            <SkipForward className="h-3 w-3" /> Skip
          </button>
        </div>
      </div>
      <style>{`
        @keyframes rest-pill-enter {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
